import React, { useMemo, useCallback, useState } from 'react';
import { useJobList, useJobDelete, useJobCreate } from '../hooks/use-job.hook';
import type { ColumnConfig } from '../../../../common/PaginatedTable';
import type { Job, JobCreatePayload } from '../types/job.types';
import JobTable from '../components/job-table.component';
import { DEFAULT_PAGINATED_TABLE_DATA } from '../../../../../const/default-paginated-table-data.const';
import ActionButtons from '../../../../common/ActionButtons';
import { useFilters } from '../../../../../hooks/use-filters.hook';
import HeaderRow from '../../../../common/HeaderRow';
import { useTranslation } from 'react-i18next';
import { useAuthUser } from '../../../../../states/user.atom';
import { rbac } from 'permissions/permissions';
import { WithPermissionCheck } from '../../../../auth/WithPermissionCheck';
import { formatDate } from '../../../../../utils/date.util';
import { Tag, message, Tabs, Button } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { extractCreateError } from '@services/extract-api-error.service';
import { LinkOutlined } from '@ant-design/icons';
import type { UseListParams } from '../../../../../types/pagination.types';
import TemplateJobCreateForm from '../components/template-job-create-form.component';
import { JobStatusEnum } from '../types/job.types';

const JobTableContainer = () => {
  const { t } = useTranslation();
  const { getUserPermissions } = useAuthUser();
  const userPermissions = getUserPermissions();
  const [activeTab, setActiveTab] = useState<'template' | 'other'>('other');

  const {
    queryParams,
    handlePageChange,
    handlePageSizeChange,
    handleFilterChange,
    handleSearchChange,
    handleSortChange,
  } = useFilters();

  // Create modified query params based on active tab
  const modifiedQueryParams = useMemo<UseListParams>(() => {
    const filterString =
      activeTab === 'template'
        ? 'filters[isTemplate][op]=eq&filters[isTemplate][val][0]=true'
        : 'filters[isTemplate][op]=eq&filters[isTemplate][val][0]=false';

    return {
      ...queryParams,
      filters: filterString,
    };
  }, [queryParams, activeTab]);

  const { data, isLoading, isFetching } = useJobList(modifiedQueryParams);
  const { docs: tableData = [] } = data ?? DEFAULT_PAGINATED_TABLE_DATA;

  const deleteJobMutation = useJobDelete();
  const createJobMutation = useJobCreate();
  const [templateJobFormVisible, setTemplateJobFormVisible] = useState(false);

  const handleCreateTemplateJob = useCallback(
    async (payload: JobCreatePayload) => {
      try {
        const finalPayload = {
          ...payload,
          failed: 0,
          total: 0,
          success: 0,
          status: JobStatusEnum.PENDING,
        };
        await createJobMutation.mutateAsync(finalPayload);
        message.success(
          t('jobs.create.success', { defaultValue: 'Template job created successfully' })
        );
        setTemplateJobFormVisible(false);
      } catch (error) {
        const errorMessage = extractCreateError(
          error,
          t('jobs.create.error.default', { defaultValue: 'Failed to create template job' })
        );
        message.error(errorMessage);
        throw error;
      }
    },
    [createJobMutation, t]
  );

  const handleDeleteJob = useCallback(
    async (record: Job) => {
      try {
        await deleteJobMutation.mutateAsync(record.id);
        message.success(t('crud.delete.success', { entity: t('jobs.title') }));
      } catch (error) {
        const errorMessage = extractCreateError(
          error,
          t('crud.delete.error', { entity: t('jobs.title') })
        );
        message.error(errorMessage);
      }
    },
    [t, deleteJobMutation]
  );

  // Handle table sorting
  const handleTableChange = useCallback(
    (pagination: any, filters: any, sorter: any) => {
      if (sorter && sorter.field) {
        // Convert antd sorter.order to our expected sortOrder format
        let sortOrder: 'asc' | 'desc';

        if (sorter.order === 'ascend') {
          sortOrder = 'asc';
        } else if (sorter.order === 'descend') {
          sortOrder = 'desc';
        } else {
          // When clicking the third time (cancel sort), cycle back to ascending
          sortOrder = 'asc';
        }

        handleSortChange(sorter.field as string, sortOrder);
      } else {
        handleSortChange('createdAt', 'desc');
      }
    },
    [handleSortChange]
  );

  // Select columns based on active tab
  const columns = useMemo<ColumnConfig<Job>[]>(() => {
    // Base columns that are common to both tabs
    const baseColumns: ColumnConfig<Job>[] = [
      {
        title: t('jobs.jobName.label'),
        key: 'jobName',
        dataIndex: 'jobName',
        sorter: true,
      },
      {
        title: t('jobs.createdBy.label'),
        key: 'createdBy',
        dataIndex: 'createdBy',
        render: (createdBy: any) => (
          <span>
            {createdBy?.name}
            {createdBy?.email && (
              <span className="text-muted d-block small">{createdBy.email}</span>
            )}
          </span>
        ),
      },
    ];

    // Columns for template jobs (show status columns)
    if (activeTab === 'template') {
      return [
        ...baseColumns,
        {
          title: t('jobs.triggerStatus.label'),
          key: 'triggerStatus',
          dataIndex: 'triggerStatus',
          render: (triggerStatus: any) => {
            if (!triggerStatus) return <span className="text-muted">-</span>;
            const statusCode = (triggerStatus as any).statusCode || triggerStatus.code || '-';
            return <span>{statusCode}</span>;
          },
          sorter: true,
        },
        {
          title: t('jobs.successStatus.label'),
          key: 'successStatus',
          dataIndex: 'successStatus',
          render: (successStatus: any) => {
            if (!successStatus) return <span className="text-muted">-</span>;
            const statusCode = (successStatus as any).statusCode || successStatus.code || '-';
            return <span>{statusCode}</span>;
          },
          sorter: true,
        },
        {
          title: t('jobs.failedBotStatus.label'),
          key: 'failedBotStatus',
          dataIndex: 'failedBotStatus',
          render: (failedBotStatus: any) => {
            if (!failedBotStatus) return <span className="text-muted">-</span>;
            const statusCode = (failedBotStatus as any).statusCode || failedBotStatus.code || '-';
            return <span>{statusCode}</span>;
          },
          sorter: true,
        },
        {
          title: t('jobs.failedDataStatus.label'),
          key: 'failedDataStatus',
          dataIndex: 'failedDataStatus',
          render: (failedDataStatus: any) => {
            if (!failedDataStatus) return <span className="text-muted">-</span>;
            const statusCode = (failedDataStatus as any).statusCode || failedDataStatus.code || '-';
            return <span>{statusCode}</span>;
          },
          sorter: true,
        },
        {
          title: t('common.createdAt.label'),
          key: 'createdAt',
          dataIndex: 'createdAt',
          render: (createdAt: string) => formatDate(createdAt),
          sorter: true,
        },
        {
          title: t('common.actions'),
          key: 'actions',
          align: 'right',
          render: (_: any, record: Job) => (
            <ActionButtons
              showEdit={false}
              showDelete={rbac.hasAccess(userPermissions, 'job.delete')}
              showView={rbac.hasAccess(userPermissions, 'job.view')}
              record={record}
              baseUrl="/dashboard/jobs"
              onDelete={() => handleDeleteJob(record)}
              isDeleting={deleteJobMutation.isPending && deleteJobMutation.variables === record.id}
            />
          ),
        },
      ];
    }

    // Columns for other jobs (show total/success/failed/email/link)
    return [
      ...baseColumns,
      {
        title: t('jobs.total.label'),
        key: 'total',
        dataIndex: 'total',
        render: (total: number, record: Job) => (record.link ? 'N/A' : total?.toString() || '0'),
        sorter: true,
      },
      {
        title: t('jobs.success.label'),
        key: 'success',
        dataIndex: 'success',
        render: (success: number, record: Job) =>
          record.link ? 'N/A' : success?.toString() || '0',
        sorter: true,
      },
      {
        title: t('jobs.failed.label'),
        key: 'failed',
        dataIndex: 'failed',
        render: (failed: number, record: Job) => (record.link ? 'N/A' : failed?.toString() || '0'),
        sorter: true,
      },
      {
        title: t('jobs.email.label'),
        key: 'email',
        dataIndex: 'email',
        render: (email: string) =>
          email ? <span>{email}</span> : <span className="text-muted">-</span>,
        sorter: true,
      },
      {
        title: t('jobs.link.label'),
        key: 'link',
        dataIndex: 'link',
        render: (link: string) =>
          link ? (
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              className="d-flex align-items-center gap-1"
            >
              <LinkOutlined />
              <span>{t('common.download')}</span>
            </a>
          ) : (
            <span className="text-muted">-</span>
          ),
      },
      {
        title: t('common.createdAt.label'),
        key: 'createdAt',
        dataIndex: 'createdAt',
        render: (createdAt: string) => formatDate(createdAt),
        sorter: true,
      },
      {
        title: t('common.actions'),
        key: 'actions',
        align: 'right',
        render: (_: any, record: Job) => (
          <ActionButtons
            showEdit={false}
            showDelete={rbac.hasAccess(userPermissions, 'job.delete')}
            showView={rbac.hasAccess(userPermissions, 'job.view')}
            record={record}
            baseUrl="/dashboard/jobs"
            onDelete={() => handleDeleteJob(record)}
            isDeleting={deleteJobMutation.isPending && deleteJobMutation.variables === record.id}
          />
        ),
      },
    ];
  }, [
    activeTab,
    t,
    userPermissions,
    handleDeleteJob,
    deleteJobMutation.isPending,
    deleteJobMutation.variables,
  ]);

  const tabItems = useMemo(
    () => [
      {
        key: 'other',
        label: t('jobs.tabs.other', { defaultValue: 'Other Jobs' }),
        children: (
          <JobTable
            data={tableData}
            columns={columns}
            loading={isLoading || isFetching || deleteJobMutation.isPending}
            pagination={data}
            rowKey="id"
            showSizeChanger={true}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            showSearchBar={true}
            onSearchChange={handleSearchChange}
            showFilters={true}
            availableFilters={[]}
            onFilterChange={handleFilterChange}
            tableProps={{
              onChange: handleTableChange,
              sortDirections: ['ascend', 'descend'],
            }}
            sortField={queryParams.sortField}
            sortOrder={queryParams.sortOrder}
          />
        ),
      },
      {
        key: 'template',
        label: t('jobs.tabs.template', { defaultValue: 'Template Jobs' }),
        children: (
          <JobTable
            data={tableData}
            columns={columns}
            loading={isLoading || isFetching || deleteJobMutation.isPending}
            pagination={data}
            rowKey="id"
            showSizeChanger={true}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            showSearchBar={true}
            onSearchChange={handleSearchChange}
            showFilters={true}
            availableFilters={[]}
            onFilterChange={handleFilterChange}
            tableProps={{
              onChange: handleTableChange,
              sortDirections: ['ascend', 'descend'],
            }}
            sortField={queryParams.sortField}
            sortOrder={queryParams.sortOrder}
          />
        ),
      },
    ],
    [
      tableData,
      columns,
      isLoading,
      isFetching,
      deleteJobMutation.isPending,
      data,
      handlePageChange,
      handlePageSizeChange,
      handleSearchChange,
      handleFilterChange,
      handleTableChange,
      queryParams.sortField,
      queryParams.sortOrder,
      t,
    ]
  );

  return (
    <>
      <HeaderRow
        title={t('crud.list.title', { entity: t('jobs.title') })}
        showCreateButton={false}
        showBackButton={false}
        customContent={
          activeTab === 'template' && rbac.hasAccess(userPermissions, 'job.create') ? (
            <Button
              type="primary"
              size="large"
              icon={<PlusOutlined />}
              onClick={() => setTemplateJobFormVisible(true)}
            >
              {t('common.create', { defaultValue: 'Create' })}
            </Button>
          ) : undefined
        }
      />

      <div className="row mt-3">
        <Tabs
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key as 'template' | 'other')}
          items={tabItems}
        />
      </div>

      <TemplateJobCreateForm
        visible={templateJobFormVisible}
        onCancel={() => setTemplateJobFormVisible(false)}
        onSubmit={handleCreateTemplateJob}
        loading={createJobMutation.isPending}
      />
    </>
  );
};

export default WithPermissionCheck(JobTableContainer, 'job.view');
